import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Scale } from "lucide-react";
import { LEGAL_DOCS, FOOTER_LINKS, LEGAL_VERSION, LAST_UPDATED, DRAFT_NOTICE, FINAL_VERSION, FINAL_EFFECTIVE, PRIVACY_VERSION, PRIVACY_EFFECTIVE } from "./legalContent";

export default function LegalPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const doc = LEGAL_DOCS[slug];
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!doc) {
    return (
      <div className="min-h-screen bg-[#0C0A0A] text-[#FBF8F3] flex flex-col items-center justify-center p-6" data-testid="legal-notfound">
        <p className="text-[#9E978E]">That legal page could not be found.</p>
        <Link to="/legal/legal-center" className="mt-3 text-[#D4AF37] underline" data-testid="legal-back-center">Go to Legal Center</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0C0A0A] text-[#FBF8F3]">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
        <button onClick={() => nav(-1)} data-testid="legal-back-btn" className="inline-flex items-center gap-1.5 text-sm text-[#9E978E] hover:text-[#FBF8F3] mb-6"><ArrowLeft size={16} /> Back</button>
        <div className="flex items-center gap-2 text-[#D4AF37] mb-1"><Scale size={18} /><span className="text-[11px] uppercase tracking-[0.22em]">DateBid Legal</span></div>
        <h1 className="font-serif text-3xl sm:text-4xl leading-tight" data-testid="legal-title">{doc.title}</h1>
        <p className="text-xs text-[#7C756C] mt-2" data-testid="legal-version">{slug === "privacy" ? `Version ${PRIVACY_VERSION} · ${PRIVACY_EFFECTIVE}` : doc.final ? (doc.reviewPending ? `Version ${FINAL_VERSION} · Pending owner and legal review` : `Version ${FINAL_VERSION} · Effective ${FINAL_EFFECTIVE}`) : `Version ${LEGAL_VERSION} · Last updated ${LAST_UPDATED}`}</p>
        {(!doc.final || doc.reviewPending) && !doc.isIndex && <p className="text-[11px] text-[#9E978E] italic mt-3 rounded-lg border border-white/8 bg-[#141113]/60 p-3" data-testid="legal-draft-notice">{DRAFT_NOTICE}</p>}

        {doc.isIndex ? (
          <LegalCenterBody />
        ) : (
          <div className="mt-6 space-y-6">
            {doc.sections.map((s, i) => (
              <section key={i} data-testid={`legal-section-${i}`}>
                <h2 className="text-lg font-semibold text-[#E0D9CC] mb-1.5">{s.h}</h2>
                <p className="text-sm text-[#C4BDB2] leading-relaxed">{s.b}</p>
              </section>
            ))}
          </div>
        )}

        <div className="mt-10 pt-6 border-t border-white/8 flex flex-wrap gap-x-5 gap-y-2 text-xs">
          <Link to="/legal/legal-center" className="text-[#D4AF37] hover:underline" data-testid="legal-footer-center">Legal Center</Link>
          <Link to="/" className="text-[#9E978E] hover:text-[#FBF8F3]" data-testid="legal-footer-home">Back to DateBid</Link>
        </div>
      </div>
    </div>
  );
}

function LegalCenterBody() {
  return (
    <div className="mt-6 space-y-7" data-testid="legal-center-body">
      {["legal", "company"].map((cat) => (
        <div key={cat}>
          <h2 className="text-[11px] uppercase tracking-[0.18em] text-[#C9A96E] mb-2">{cat === "legal" ? "Policies & Legal" : "Company"}</h2>
          <ul className="space-y-1.5">
            {FOOTER_LINKS[cat].filter((l) => l.slug !== "legal-center").map((l) => (
              <li key={l.slug}>
                <Link to={l.path || `/legal/${l.slug}`} data-testid={`legal-index-${l.slug}`} className="text-sm text-[#D8D2C9] hover:text-[#D4AF37] transition-colors">{l.label}</Link>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
